import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import {
  UserCog,
  Plus,
  MoreHorizontal,
  Shield,
  ShieldCheck,
  HeadphonesIcon,
  X,
  Loader2,
  Copy,
  Check,
} from 'lucide-react'

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: 'super_admin' | 'admin' | 'support'
  is_active: number
  must_change_password: number
  last_login_at: string | null
  created_at: string
}

interface AdminsResponse {
  admins: AdminUser[]
}

interface CreateAdminResponse {
  admin: AdminUser
  temporaryPassword: string
  message: string
}

export function AdminUsersPage() {
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [tempPassword, setTempPassword] = useState('')
  const [copiedPassword, setCopiedPassword] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', role: 'admin' as const })
  const [error, setError] = useState('')
  const { user: currentUser } = useAuthStore()

  const isSuperAdmin = currentUser?.role === 'super_admin'

  useEffect(() => {
    fetchAdmins()
  }, [])

  async function fetchAdmins() {
    setLoading(true)
    try {
      const data = await api.get<AdminsResponse>('/users')
      setAdmins(data.admins)
    } catch (err) {
      setError('Failed to fetch admin users')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateAdmin(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)
    setError('')

    try {
      const response = await api.post<CreateAdminResponse>('/users', newAdmin)
      setTempPassword(response.temporaryPassword)
      setShowCreateModal(false)
      setShowPasswordModal(true)
      setNewAdmin({ email: '', name: '', role: 'admin' })
      fetchAdmins()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create admin')
    } finally {
      setCreating(false)
    }
  }

  async function handleDeactivate(adminId: string) {
    if (!confirm('Are you sure you want to deactivate this admin user?')) return

    try {
      await api.post(`/users/${adminId}/deactivate`, {})
      fetchAdmins()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate admin')
    }
  }

  const copyPassword = async () => {
    await navigator.clipboard.writeText(tempPassword)
    setCopiedPassword(true)
    setTimeout(() => setCopiedPassword(false), 2000)
  }

  const getRoleBadge = (role: AdminUser['role']) => {
    const styles = {
      super_admin: {
        className: 'bg-red-500/10 text-red-500',
        icon: ShieldCheck,
        label: 'Super Admin',
      },
      admin: {
        className: 'bg-blue-500/10 text-blue-500',
        icon: Shield,
        label: 'Admin',
      },
      support: {
        className: 'bg-green-500/10 text-green-500',
        icon: HeadphonesIcon,
        label: 'Support',
      },
    }
    const style = styles[role]
    const Icon = style.icon

    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${style.className}`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </span>
    )
  }

  const getStatusBadge = (isActive: number) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-green-500/10 text-green-500">
          Active
        </span>
      )
    }
    return (
      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-red-500/10 text-red-500">
        Inactive
      </span>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Users</h1>
          <p className="text-muted-foreground">
            Manage platform administrators
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Admin
          </button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive-foreground rounded-lg p-3 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-sm underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                {isSuperAdmin && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">
                    No admin users found
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <UserCog className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{admin.name || 'Unnamed'}</p>
                          <p className="text-xs text-muted-foreground">
                            {admin.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getRoleBadge(admin.role)}</td>
                    <td className="px-4 py-3">{getStatusBadge(admin.is_active)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {admin.last_login_at ? formatDate(admin.last_login_at) : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(admin.created_at)}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-right">
                        {admin.id !== currentUser?.id && admin.is_active === 1 && (
                          <button
                            onClick={() => handleDeactivate(admin.id)}
                            className="text-sm text-destructive hover:underline"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add New Admin</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 rounded hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  required
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="admin@example.com"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  required
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Admin Name"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="role" className="text-sm font-medium">
                  Role
                </label>
                <select
                  id="role"
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value as 'admin' | 'support' })}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Admin Created Successfully</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Share this temporary password with the new admin. They will be required to change it on first login.
            </p>

            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg mb-4">
              <code className="flex-1 font-mono text-sm">{tempPassword}</code>
              <button
                onClick={copyPassword}
                className="p-2 rounded hover:bg-accent"
              >
                {copiedPassword ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>

            <button
              onClick={() => {
                setShowPasswordModal(false)
                setTempPassword('')
              }}
              className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
